import { useId, useState, forwardRef, type InputHTMLAttributes } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Testo di aiuto sotto la label, collegato con aria-describedby. */
  description?: string;
  /** Errore di validazione: collegato con aria-describedby + aria-invalid. */
  error?: string | undefined;
  /** Solo per type="password": aggiunge il pulsante Mostra/Nascondi. */
  revealable?: boolean;
}

/**
 * Campo di testo accessibile (lineeguida §48):
 * label sempre visibile, descrizione ed errore collegati via aria-describedby,
 * required comunicato sia visivamente sia semanticamente.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, description, error, required, id: idProp, className, revealable, type, ...inputProps },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const descriptionId = `${id}-desc`;
  const errorId = `${id}-err`;
  const [revealed, setRevealed] = useState(false);
  const withToggle = revealable && type === 'password';

  const describedBy =
    [description ? descriptionId : null, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  const input = (
    <input
      ref={ref}
      id={id}
      type={withToggle && revealed ? 'text' : type}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      className={`rounded-lg border px-3 py-2.5 text-base bg-white text-ink-900 dark:bg-ink-900 dark:text-ink-50 ${
        error
          ? 'border-danger border-2'
          : 'border-ink-300 dark:border-ink-600'
      } ${withToggle ? 'w-full pr-14' : ''} ${className ?? ''}`}
      {...inputProps}
    />
  );

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
        {required && (
          <span className="text-danger">
            {' '}
            *<span className="visually-hidden"> (obbligatorio)</span>
          </span>
        )}
      </label>
      {description && (
        <p id={descriptionId} className="text-sm text-ink-600 dark:text-ink-300">
          {description}
        </p>
      )}
      {withToggle ? (
        <div className="relative">
          {input}
          <button
            type="button"
            aria-pressed={revealed}
            aria-label={revealed ? 'Nascondi password' : 'Mostra password'}
            onClick={() => setRevealed((v) => !v)}
            className="absolute inset-y-0 right-1 flex items-center rounded-lg px-2 text-xl"
          >
            <span aria-hidden="true">{revealed ? '🙈' : '👁'}</span>
          </button>
        </div>
      ) : (
        input
      )}
      {error && (
        <p id={errorId} className="flex items-start gap-1 text-sm font-medium text-danger">
          <span aria-hidden="true">⚠</span>
          <span>Errore: {error}</span>
        </p>
      )}
    </div>
  );
});
